/**
 * The OPC UA `NodeId` string encoding, as LiftCloud parses it.
 *
 * This is a **wire format**, not a display format, and the distinction is the
 * whole point of this module. The bank-scoped node -> lift binding authored in
 * `BankNodeLiftMatrix` is keyed by these strings; the keys are packed into
 * `Bank.opc_ua_node_lift_mapping_binary` and parsed on the other side by
 * LiftCloud's `parseOpcUaNodeIDString`
 * (`utils/flatbuffers/generation/opcua_bank_binding.go`), which builds a typed
 * `OpcUa.NodeId` from them and hands it to VTS.
 *
 * That parser accepts the standard IEC 62541-3 §5.3.1 short forms and nothing
 * else — `i` numeric, `s` string, `g` GUID — and **drops any entry it cannot
 * parse with a warning rather than an error**. A producer that emits a
 * different spelling therefore does not fail loudly: the binding silently
 * arrives empty, VTS resolves every node to no lift, and the bank connects and
 * publishes nothing. That is exactly the failure this module exists to make
 * impossible, so treat the prefix table below as a contract with Go, not as a
 * local formatting choice.
 */
import type {
  OpcUaNodeIdForm,
  OpcUaNodeIdentifierKind,
} from "../interfaces/AdapterCdoOpcUa.interface";

/**
 * Identifier-kind -> OPC UA string prefix.
 *
 * `null` marks a kind this encoding cannot express in a form LiftCloud will
 * accept, so it can never be bound to a lift:
 *
 * - `bytestring` — the standard spells opaque identifiers `b=<base64>`, but the
 *   form stores them as comma-separated decimal bytes (`"1,2,255"`), and
 *   LiftCloud's parser has no `b` case at all. Both halves would have to change
 *   together.
 * - `browsepath` — not a `NodeId` at all. It is a path resolved against the
 *   server at connect time, so it has no stable identifier to key a binding on.
 */
export const OPC_UA_NODE_ID_PREFIX: Record<OpcUaNodeIdentifierKind, string | null> = {
  numeric: "i",
  string: "s",
  guid: "g",
  bytestring: null,
  browsepath: null,
};

/**
 * True when a node can be encoded as a `NodeId` string LiftCloud will parse,
 * and therefore bound to a lift.
 */
export function isBindableNode(node: OpcUaNodeIdForm): boolean {
  return OPC_UA_NODE_ID_PREFIX[node.identifierKind] !== null;
}

/**
 * Encode a node as an OPC UA `NodeId` string (`ns=<u16>;<prefix>=<identifier>`).
 *
 * Returns `null` for kinds that cannot be expressed — callers must handle that
 * rather than substituting a placeholder, because a placeholder is precisely
 * the silently-dropped key this module prevents.
 */
export function opcUaNodeIdString(node: OpcUaNodeIdForm): string | null {
  const prefix = OPC_UA_NODE_ID_PREFIX[node.identifierKind];
  if (prefix === null) return null;
  return `ns=${node.namespaceIndex};${prefix}=${node.identifier}`;
}

/**
 * Human-readable label for a node that has no `NodeId` encoding, so the
 * operator can still tell which mapping row a matrix entry refers to.
 */
export function describeUnbindableNode(node: OpcUaNodeIdForm): string {
  if (node.identifierKind === "browsepath") {
    return `browsepath(${(node.hops ?? []).map((h) => h.targetName).join("/")})`;
  }
  return `ns=${node.namespaceIndex};${node.identifierKind}=${node.identifier}`;
}
